import { useState } from 'react';

import { toUserMessage } from '@platform/api-client';

import {
  useAttachPumsemDivision,
  useCreatePumsemDivision,
  useDetachPumsemDivision,
  useReorderPumsemDivisions,
  useUpdatePumsemDivision,
} from '../../api/use-pumsem-manage.query';
import type { PumsemEditionDivision } from '../../model/pumsemManage.type';
import { BTN_GHOST, BTN_PRIMARY, FIELD, LABEL } from './pumsemFormStyles';

interface PumsemDivisionPanelProps {
  /** 이 판에 실린 부문. 순서대로 온다. */
  divisions: PumsemEditionDivision[];
  /** 다른 판에는 있는데 이 판에는 안 실린 부문. 다시 실을 수 있게 보여준다. */
  available: { slug: string; title: string }[];
  edition: string;
}

/** 순서 배열에서 한 칸 옮긴다. 끝에서 더 밀면 그대로 둔다. */
const moveAt = (list: string[], from: number, to: number): string[] => {
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  const [picked] = next.splice(from, 1);
  next.splice(to, 0, picked);
  return next;
};

/**
 * 부문 편성 — **이 판에** 어떤 부문이 어떤 순서로 실리는지.
 *
 * ── 부문 자체와 편성은 다른 것이다 ──────────────────────────
 * 부문 행(`machine`)은 판을 가로지르는 전역 정체성이고 `/pumsem/machine/6-2-4`
 * 딥링크의 첫 세그먼트다. 지우면 그 아래 모든 링크가 죽는다. 실무에서 필요한
 * 건 대개 "이번 판에는 안 싣는다" 쪽이고, 그건 **판에서 빼기**다 —
 * 데이터는 그대로 두고 편성만 끊는다.
 *
 * 순서는 판마다 다를 수 있어 `pumsem_edition_divisions` 가 갖는다. 부문 순서는
 * 코드를 바꾸지 않으므로 **공개판에서도** 옮길 수 있다(장·절 재정렬과 다르다).
 */
export function PumsemDivisionPanel({
  divisions,
  available,
  edition,
}: PumsemDivisionPanelProps) {
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({
    slug: '',
    title: '',
    shortTitle: '',
    remark: '',
  });
  const [editing, setEditing] = useState<string | null>(null);
  const [detaching, setDetaching] = useState<string | null>(null);
  const [patch, setPatch] = useState({
    title: '',
    shortTitle: '',
    remark: '',
  });

  const createDivision = useCreatePumsemDivision();
  const updateDivision = useUpdatePumsemDivision();
  const attachDivision = useAttachPumsemDivision(edition);
  const detachDivision = useDetachPumsemDivision(edition);
  const reorderDivisions = useReorderPumsemDivisions(edition);

  const onError = (cause: unknown) => setError(toUserMessage(cause));
  const order = divisions.map((division) => division.slug);

  const move = (index: number, delta: number) => {
    const next = moveAt(order, index, index + delta);
    if (next === order) return;
    reorderDivisions.mutate(next, { onError });
  };

  return (
    <section className="mb-8 rounded-xl border border-neutral-200 p-4 dark:border-white/12">
      <h2 className={LABEL}>이 판의 부문</h2>

      <ul className="mb-3 space-y-1">
        {divisions.map((division, index) => (
          <li
            key={division.slug}
            className="flex flex-wrap items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-neutral-50 dark:hover:bg-white/5"
          >
            <span className="flex shrink-0 gap-1 text-[12px] text-neutral-400">
              <button
                type="button"
                aria-label={`${division.title} 위로`}
                disabled={index === 0 || reorderDivisions.isPending}
                className="hover:text-neutral-700 disabled:opacity-30"
                onClick={() => move(index, -1)}
              >
                ↑
              </button>
              <button
                type="button"
                aria-label={`${division.title} 아래로`}
                disabled={
                  index === divisions.length - 1 || reorderDivisions.isPending
                }
                className="hover:text-neutral-700 disabled:opacity-30"
                onClick={() => move(index, 1)}
              >
                ↓
              </button>
            </span>

            <code className="w-24 shrink-0 text-[12px] text-neutral-400">
              {division.slug}
            </code>

            {editing === division.slug ? (
              <>
                <input
                  className={`${FIELD} w-56`}
                  value={patch.title}
                  aria-label="부문 정식 명칭"
                  onChange={(event) =>
                    setPatch((prev) => ({ ...prev, title: event.target.value }))
                  }
                />
                <input
                  className={`${FIELD} w-24`}
                  value={patch.shortTitle}
                  aria-label="부문 짧은 이름"
                  onChange={(event) =>
                    setPatch((prev) => ({
                      ...prev,
                      shortTitle: event.target.value,
                    }))
                  }
                />
                {/* 비워서 저장하면 매핑이 끊긴다 — XCOST 가 이 부문 코드를
                    보내도 더 이상 링크가 안 만들어진다. */}
                <input
                  className={`${FIELD} w-16 uppercase`}
                  value={patch.remark}
                  maxLength={1}
                  placeholder="P"
                  aria-label="XCOST REMARK"
                  onChange={(event) =>
                    setPatch((prev) => ({
                      ...prev,
                      remark: event.target.value.toUpperCase(),
                    }))
                  }
                />
                <button
                  type="button"
                  className={BTN_PRIMARY}
                  disabled={
                    !patch.title.trim() ||
                    !patch.shortTitle.trim() ||
                    updateDivision.isPending
                  }
                  onClick={() =>
                    updateDivision.mutate(
                      {
                        slug: division.slug,
                        patch: {
                          title: patch.title.trim(),
                          shortTitle: patch.shortTitle.trim(),
                          // 빈 문자열을 그대로 보낸다 = 매핑 끊기.
                          remark: patch.remark.trim(),
                        },
                      },
                      { onSuccess: () => setEditing(null), onError },
                    )
                  }
                >
                  저장
                </button>
                <button
                  type="button"
                  className={BTN_GHOST}
                  onClick={() => setEditing(null)}
                >
                  취소
                </button>
              </>
            ) : (
              <>
                <span className="text-[13.5px] text-neutral-800 dark:text-neutral-100">
                  {division.title}
                </span>
                <span className="rounded bg-neutral-100 px-1.5 py-0.5 text-[11.5px] text-neutral-600 dark:bg-white/8 dark:text-neutral-300">
                  {division.shortTitle}
                </span>
                {/* REMARK 가 비어 있으면 XCOST 링크가 이 부문에 못 닿는다.
                    조용히 비워 두면 아무도 모르므로 목록에서 표시한다. */}
                {division.remark ? (
                  <span
                    className="rounded border border-brand-200 px-1.5 py-0.5 font-mono text-[11.5px] font-bold text-brand-700 dark:border-brand-500/30 dark:text-brand-300"
                    title="XCOST 코드 첫 글자"
                  >
                    {division.remark}
                  </span>
                ) : (
                  <span
                    className="rounded border border-dashed border-neutral-300 px-1.5 py-0.5 text-[11.5px] text-neutral-400 dark:border-white/15"
                    title="XCOST 코드 첫 글자가 비어 있어 링크 대상이 아닙니다"
                  >
                    REMARK 없음
                  </span>
                )}
                <span className="text-[12px] text-neutral-400">
                  이 판 {division.itemCount}건
                </span>
                <span className="ml-auto flex gap-2">
                  <button
                    type="button"
                    className="text-[12.5px] font-bold text-brand-700 hover:underline dark:text-brand-300"
                    onClick={() => {
                      setError(null);
                      setPatch({
                        title: division.title,
                        shortTitle: division.shortTitle,
                        remark: division.remark ?? '',
                      });
                      setEditing(division.slug);
                    }}
                  >
                    이름 수정
                  </button>
                  {/* 부문을 지우는 게 아니라 이 판에서 빼는 것이다. 항목이
                      남아 있으면 서버가 건수를 적어 거절하지만, 그건 서버
                      사정이고 화면에는 사전 고지가 있어야 한다 — `이름 수정`
                      바로 옆 같은 크기의 버튼이라 오클릭이 쉽다. */}
                  {detaching === division.slug ? (
                    <>
                      <button
                        type="button"
                        className="text-[12.5px] font-bold text-danger hover:underline"
                        disabled={detachDivision.isPending}
                        onClick={() =>
                          detachDivision.mutate(division.slug, {
                            onSuccess: () => setDetaching(null),
                            onError,
                          })
                        }
                      >
                        {detachDivision.isPending ? '빼는 중…' : '정말 빼기'}
                      </button>
                      <button
                        type="button"
                        className="text-[12.5px] text-neutral-400 hover:text-neutral-700"
                        onClick={() => setDetaching(null)}
                      >
                        취소
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="text-[12.5px] text-neutral-400 hover:text-danger"
                      onClick={() => {
                        setError(null);
                        setDetaching(division.slug);
                      }}
                    >
                      이 판에서 빼기
                    </button>
                  )}
                </span>
              </>
            )}
          </li>
        ))}
      </ul>

      {divisions.length === 0 && (
        <p className="mb-3 rounded-xl border border-dashed border-neutral-200 px-4 py-6 text-center text-[13px] text-neutral-400 dark:border-white/12">
          이 판에 실린 부문이 없습니다. 아래에서 실어 주세요.
        </p>
      )}

      {available.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="text-[12.5px] text-neutral-500">
            다른 판에 있는 부문
          </span>
          {available.map((division) => (
            <button
              key={division.slug}
              type="button"
              className={BTN_GHOST}
              disabled={attachDivision.isPending}
              onClick={() => attachDivision.mutate(division.slug, { onError })}
            >
              + {division.title}
            </button>
          ))}
        </div>
      )}

      {adding ? (
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <span className={LABEL}>주소용 영문 이름</span>
            <input
              className={`${FIELD} w-32`}
              value={draft.slug}
              placeholder="machine"
              aria-label="부문 주소용 영문 이름"
              onChange={(event) =>
                setDraft((prev) => ({ ...prev, slug: event.target.value }))
              }
            />
          </div>
          <div className="min-w-52 flex-1">
            <span className={LABEL}>정식 명칭</span>
            <input
              className={FIELD}
              value={draft.title}
              placeholder="공통부문, 토목부문, 건축부문 …"
              aria-label="부문 정식 명칭"
              onChange={(event) =>
                setDraft((prev) => ({ ...prev, title: event.target.value }))
              }
            />
          </div>
          <div>
            <span className={LABEL}>짧은 이름</span>
            <input
              className={`${FIELD} w-24`}
              value={draft.shortTitle}
              placeholder="기계"
              aria-label="부문 짧은 이름"
              onChange={(event) =>
                setDraft((prev) => ({ ...prev, shortTitle: event.target.value }))
              }
            />
          </div>
          <div>
            <span className={LABEL}>XCOST REMARK</span>
            <input
              className={`${FIELD} w-20 uppercase`}
              value={draft.remark}
              maxLength={1}
              placeholder="M"
              aria-label="XCOST REMARK"
              onChange={(event) =>
                setDraft((prev) => ({
                  ...prev,
                  remark: event.target.value.toUpperCase(),
                }))
              }
            />
          </div>
          <button
            type="button"
            className={BTN_PRIMARY}
            disabled={
              !draft.slug.trim() ||
              !draft.title.trim() ||
              !draft.shortTitle.trim() ||
              createDivision.isPending
            }
            onClick={() =>
              createDivision.mutate(
                {
                  slug: draft.slug.trim(),
                  title: draft.title.trim(),
                  shortTitle: draft.shortTitle.trim(),
                  remark: draft.remark.trim() || undefined,
                  // 만들자마자 이 판에 싣는다. 안 실으면 트리에 안 보여서
                  // "안 만들어졌다" 로 읽힌다.
                  attachTo: edition || undefined,
                },
                {
                  onSuccess: () => {
                    setAdding(false);
                    setDraft({
                      slug: '',
                      title: '',
                      shortTitle: '',
                      remark: '',
                    });
                  },
                  onError,
                },
              )
            }
          >
            부문 만들기
          </button>
          <button
            type="button"
            className={BTN_GHOST}
            onClick={() => setAdding(false)}
          >
            취소
          </button>
        </div>
      ) : (
        <button
          type="button"
          className={BTN_GHOST}
          onClick={() => setAdding(true)}
        >
          새 부문 만들기
        </button>
      )}

      <p className="mt-2 text-[12px] text-neutral-500">
        부문 자체는 모든 판이 공유합니다. 여기서 정하는 건 <b>이 판에 실을지와
        순서</b>뿐이고, 빼도 데이터와 링크는 남습니다. 주소용 영문 이름은{' '}
        <code>/pumsem/machine/…</code> 의 첫 조각으로 쓰이며 만든 뒤에 바꿀 수
        없습니다. <b>XCOST REMARK</b> 는 XCOST 품셈 코드의 첫 글자로(
        <code>P010102</code> → <code>/common/1-1-2</code>), 비워 두면 그 부문은
        XCOST 링크 대상이 아닙니다.
      </p>

      {error && (
        <p className="mt-2 text-[13px] text-danger" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}
